import { createPolicy } from "./policy";

/**
 * One entry per protected route in App.js. Matching is done on whole path
 * segments (never substrings): "/auth/menu" does not match "/auth/menu-old",
 * and ":param" segments match any single segment.
 *
 *   id          stable key App.js uses to pick the page component
 *   path        absolute pattern
 *   permission  what the user must hold to open the route
 *   nav         optional sidebar entry: { permission }. The sidebar order is
 *               the order of this array. Defaults to the route permission.
 *   resource    optional (params) => resource, for scoped routes
 */
export const ROUTE_POLICIES = Object.freeze([
  // Sidebar order is the order of this array: the dashboard first (requested), then
  // restaurants; delivery documentation after the admin entries. Do not reorder
  // without approval.
  { id: "lud-dashboard", path: "/auth/lud-dashboard", permission: "ludDashboard.view", nav: { permission: "ludDashboard.nav" } },
  { id: "restaurants", path: "/auth/restaurants", permission: "restaurant.view" },
  { id: "menu", path: "/auth/menu", permission: "menu.view" },
  { id: "campaignRecord", path: "/auth/campaignRecord", permission: "campaign.view" },
  { id: "operation", path: "/auth/operation", permission: "operation.view" },
  { id: "faults", path: "/auth/faults", permission: "fault.view" },
  { id: "reports", path: "/auth/reports", permission: "report.view" },
  { id: "saled-items", path: "/auth/saled-items", permission: "saledItem.view" },
  { id: "analysis", path: "/auth/analysis", permission: "analysis.view" },
  { id: "lud-documents", path: "/auth/lud-documents", permission: "ludDocument.view" },
  {
    id: "notifications",
    path: "/auth/notifications",
    permission: "notification.view",
    nav: { permission: "notification.nav" },
  },
  {
    id: "support",
    path: "/auth/support",
    permission: "support.view",
    nav: { permission: "support.nav" },
  },
  // LUD Copilot support inbox (ceo-level + am / dm / dmm); the conversation scope is
  // enforced by the backend.
  { id: "copilot", path: "/auth/copilot", permission: "copilot.view", nav: { permission: "copilot.nav" } },
  {
    id: "guidelines",
    path: "/auth/guidelines",
    permission: "guideline.view",
    nav: { permission: "guideline.nav" },
  },
  { id: "onboarding", path: "/auth/onboarding", permission: "onboarding.view" },
  { id: "crm", path: "/auth/crm", permission: "crm.view" },
  {
    id: "store-onboarding-admin",
    path: "/auth/store-onboarding-admin",
    permission: "storeOnboardingAdmin.view",
  },
  { id: "lud-connect", path: "/auth/lud-connect", permission: "ludConnect.view" },
  { id: "lud", path: "/auth/lud", permission: "lud.view" },
  { id: "users", path: "/auth/users", permission: "user.view" },
  { id: "logs", path: "/auth/logs", permission: "log.view" },
  // Email Inbox: the administrative sent-email history (ceo / cto / developer).
  { id: "mail-inbox", path: "/auth/emails", permission: "mail.view" },
  {
    id: "delivery-documentation-admin",
    path: "/auth/delivery-documentation-admin",
    permission: "deliveryDocumentation.admin.view",
  },
  {
    id: "delivery-documentation",
    path: "/auth/delivery-documentation",
    permission: "deliveryDocumentation.view",
  },
  {
    id: "billing",
    path: "/auth/billing",
    permission: "billing.view",
    or: "billing.accountant.view",
    nav: { permission: "billing.nav" },
  },
  // Internal UI component showcase. Same layout as the /auth shell, at its own path.
  { id: "test-ui", path: "/test-ui", permission: "app.testUi" },
  // Outside the /auth shell: the new-client wizard.
  {
    id: "store-onboarding",
    path: "/auth/store-onboarding",
    permission: "onboarding.store.view",
    nav: false,
  },
]);

// The /auth shell itself (Admin layout + sidebar).
export const AUTH_AREA_POLICY = Object.freeze({
  id: "auth-area",
  path: "/auth",
  permission: "app.enter",
});

/** Can this policy's `can` open the route? Its `permission`, or its optional alternative `or`. */
export const canRoute = (can, route, resource = route?.resource) =>
  Boolean(route) && [route.permission, route.or].some((id) => Boolean(id) && can(id, resource));

const splitPath = (path) => String(path || "").split("?")[0].split("#")[0]
  .split("/").filter(Boolean);

/** Segment-wise match. Returns the params object or null. */
export const matchPath = (pattern, pathname) => {
  const want = splitPath(pattern);
  const have = splitPath(pathname);
  if (want.length !== have.length) return null;
  const params = {};
  for (let i = 0; i < want.length; i += 1) {
    if (want[i].startsWith(":")) params[want[i].slice(1)] = decodeURIComponent(have[i]);
    else if (want[i] !== have[i]) return null;
  }
  return params;
};

/** Finds the policy for a pathname, or null when no protected route matches. */
export const resolveRoutePolicy = (pathname) => {
  for (const policy of ROUTE_POLICIES) {
    const params = matchPath(policy.path, pathname);
    if (params) {
      return {
        ...policy,
        params,
        resource: policy.resource ? policy.resource(params) : undefined,
      };
    }
  }
  return null;
};

/** Sidebar entries the given `can` may see, in sidebar order. */
export const visibleNavItems = (can) =>
  ROUTE_POLICIES.filter((policy) => {
    if (policy.nav === false) return false;
    return policy.nav?.permission ? can(policy.nav.permission) : canRoute(can, policy);
  });

const DASHBOARD_HOME = "/auth/lud-dashboard";
const ACCOUNTANT_HOME = "/auth/billing";

/**
 * Where a signed-in user lands (after login, refresh of "/", from the logo).
 * The accountant (fa) lands on Billing, its workspace; other users who can open
 * the dashboard land there; everyone else lands on the first
 * sidebar entry they may open. A `new` client opens the same dashboard as a
 * store. Never a route the user cannot open, so a RouteGuard redirect to this
 * path cannot loop.
 */
export const homeRouteFor = (user, isAuthenticated = true) => {
  const policy = createPolicy({ isAuthenticated, user });
  if (!policy.isReady) return null;
  const allowed = (path) => {
    const route = resolveRoutePolicy(path);
    return canRoute(policy.can, route);
  };
  if (policy.can("ludDashboard.mode.accountant") && allowed(ACCOUNTANT_HOME)) return ACCOUNTANT_HOME;
  if (allowed(DASHBOARD_HOME)) return DASHBOARD_HOME;
  const first = visibleNavItems(policy.can)[0] || ROUTE_POLICIES.find((r) => allowed(r.path));
  return first ? first.path : null;
};
